import React, { useEffect, useState } from 'react';
import axiosInstance from '../../../../Services/axiosCommon';
import { useFormik } from 'formik';
import { useToast } from '../../../../Components/Others/ToastServices';
import { TextMediumXS, TextMediumSM } from '../../../../Components/Text/MediumText';
import { MultiSelectSet } from '../../../../Components/Utils/MultiSelect';
import UseDropDown from '../../../../Components/Hooks/useDropDown';
import { SaveButton } from '../../../../Components/Utils/Buttons';
import { GetAccess } from '../../../../Components/Utils/roles';
import { ShieldUser } from 'lucide-react';
import { HeadingWithIcon } from '../../../../Components/Utils/Settingheading';

function InspectorSetting() {
  const { data: user } = UseDropDown(`/user/new-drop-down`);
  const [data, setData] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [allSelectedUserIds, setAllSelectedUserIds] = useState(new Set());
  const toast = useToast();

  const formik = useFormik({
    initialValues: {
      users: []
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        
        // Add newly selected users to our complete set
        const newSelectedIds = selectedUsers.map(user => user.value);
        const updatedSelectedIds = new Set([...allSelectedUserIds, ...newSelectedIds]);
         
        // Convert to array for API call
        const userIdsToSubmit = Array.from(updatedSelectedIds);
        
        const response = await axiosInstance.post(`/inspectors/`, {
          users: userIdsToSubmit
        });

        if (response.data) {
          toast.open({
            type: "success",
            message: "Inspectors added successfully",
          });
          resetForm();
          setSelectedUsers([]);
          setAllSelectedUserIds(updatedSelectedIds);
          GetData();
        }
      } catch (error) {
        toast.open({
          type: "error",
          message: "Failed to add inspectors",
        });
      } finally {
        setLoading(false);
      }
    }
  });

  const GetData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/inspectors/`);
      let inspectorList = response.data?.data || [];

      // Sort alphabetically by userName
      inspectorList.sort((a, b) => {
        const nameA = a.userName?.toLowerCase() || "";
        const nameB = b.userName?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      });

      // Update our complete set of selected user IDs
      const currentIds = new Set(inspectorList.map(inspector => inspector.userId));
      setAllSelectedUserIds(currentIds);
      
      setData(inspectorList);
    } catch (error) {
      toast.open({
        type: 'error',
        message: 'Failed to fetch inspectors',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(`/inspectors/${id}`);
      if (response.status === 200) {
        toast.open({
          type: "success",
          message: "Inspector deleted successfully",
        });
        
        // Remove from our complete set
        const updatedSelectedIds = new Set(allSelectedUserIds);
        updatedSelectedIds.delete(id);
        setAllSelectedUserIds(updatedSelectedIds);
        
        GetData();
      }
    } catch (error) {
      toast.open({
        type: "error",
        message: "Failed to delete inspector",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (selectedOptions) => {
    setSelectedUsers(selectedOptions);
  };

  // Filter dropdown options excluding already added inspectors
  useEffect(() => {
    const filtered = user?.filter(u => !allSelectedUserIds.has(u.value));
    setFilteredUsers(filtered);
  }, [user, allSelectedUserIds]);

  useEffect(() => {
    GetData();
  }, []);

  return (
    <div className='w-full px-3'>
   
             <HeadingWithIcon icon={ShieldUser} title="Inspectors" />



      <form onSubmit={formik.handleSubmit}>
        <div className="w-full text-xs flex"> 
          <div className="mb-1 w-full sm:w-[55%]">
            <MultiSelectSet
              type="text"
              Options={filteredUsers}
              placeholder="Choose inspectors"
              value={selectedUsers}
              onChange={handleUserSelect}
              isMulti={true}
              formik={formik}
              name="users"
            />
          </div>
          {GetAccess('inspector_create') &&
            <div className='w-[10%] mt-4'>
              <button
                type="submit"
                className="bg-baseColor-primary text-white rounded-md px-3 py-2 hover:bg-green-800 transition-colors focus:outline-none"
                disabled={loading || selectedUsers.length === 0}
              >
                +
              </button>
            </div>}
        </div>
      </form>

      <div className="">
        <div className="sm:w-[55%] mx-6 rounded-lg h-48 bg-[#f5f3ff] border border-gray-300 overflow-x-hidden overflow-y-auto">
          {data.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              No inspectors added yet
            </div>
          ) : (
            <ul>
              {data.map((inspector, idx) => (
                <li
                  className="m-1 flex justify-between items-center text-xs bg-white border border-gray-300 rounded text-main-title py-2 px-2 hover:bg-[#B3EBD7] hover:bg-opacity-50"
                  key={inspector.id || idx}
                >
                  <TextMediumXS
                    color="text-[#697565]"
                    content={inspector.userName || "No name"}
                  />
                  {GetAccess('inspector_delete') && <>
                    {!inspector?.isDefault && (
                      <span
                        onClick={() => handleDelete(inspector.id)}
                        className="text-blue-900 cursor-pointer hover:text-red-600"
                      >
                        ×
                      </span>
                    )}
                  </>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {GetAccess('inspector_create') &&
        <div className='mt-4 ml-5'>
          <SaveButton
            handleClick={GetData}
            svg={false}
            disabled={loading}
            text={loading ? "Refreshing..." : "Refresh List"}
          />
        </div>}
    </div>
  )
}

export default InspectorSetting;
